import { useEffect, useMemo, useRef } from 'react'
import { Link } from 'react-router-dom'
import {
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  parseISO,
  startOfMonth,
  startOfWeek,
} from 'date-fns'
import { Loader2, Palmtree } from 'lucide-react'

import Alert from '@/components/ui/Alert'
import { apiErrorMessage } from '@/lib/api'
import { cn } from '@/lib/cn'
import { usePrefersReducedMotion } from '@/lib/use-media-query'
import { useAdminSessions } from '@/features/admin/use-admin-sessions'
import { useAdminVacationRequests } from '@/features/admin/use-admin-vacation'
import {
  type WeekendActivityEntry,
  sessionDayIso,
  useTodayIso,
  weekendEntriesFrom,
} from '@/features/admin/use-calendar-week'
import WeekendActivityNotice from '@/features/admin/weekend-activity-notice'

const WEEK_START = 1 as const
// Klinika pracuje pn–pt, więc siatka ma pięć kolumn, nie siedem. Efekt uboczny
// jest na telefonie wyraźny: komórka rośnie z ~48 px do ~66 px, czyli tyle,
// żeby liczby przestały się łamać.
const WEEKDAY_LABELS = ['pon', 'wt', 'śr', 'czw', 'pt']
const WEEKEND_DOW = new Set([0, 6])

function pluralPersons(n: number): string {
  if (n === 1) return '1 osoba'
  // Reguła musi patrzeć na końcówkę, nie na sam zakres 2–4: przy rosterze
  // 10–25 osób poprzednia wersja pisała „22 osób" zamiast „22 osoby".
  const last = n % 10
  const lastTwo = n % 100
  if (last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14)) {
    return `${n} osoby`
  }
  return `${n} osób`
}

function formatHours(seconds: number): string {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  if (h === 0 && m === 0) return ''
  if (h === 0) return `${m}min`
  if (m === 0) return `${h}h`
  return `${h}h ${m}min`
}

/** Wariant bez spacji na komórkę szerokości ~48 px na telefonie. */
function formatHoursCompact(seconds: number): string {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  if (h === 0 && m === 0) return ''
  if (h === 0) return `${m}m`
  if (m === 0) return `${h}h`
  return `${h}h${String(m).padStart(2, '0')}`
}

interface DayAggregate {
  uniqueUsers: Set<string>
  totalSeconds: number
  hasOpenSession: boolean
}

interface Props {
  cursor: Date
  focusToken: number
}

export default function CalendarMonthView({ cursor, focusToken }: Props) {
  const today = useTodayIso()
  const reducedMotion = usePrefersReducedMotion()
  const todayCellRef = useRef<HTMLAnchorElement>(null)
  const gridRef = useRef<HTMLDivElement>(null)

  // Zakres siatki jako STRINGI — `startOfWeek` zwraca nowy obiekt Date przy
  // każdym renderze, więc useMemo na nim nigdy nie trafiał w cache i cała
  // siatka plus mapa urlopów przeliczały się na każdym przerenderowaniu.
  const { gridStartIso, gridEndIso } = useMemo(() => {
    const monthStart = startOfMonth(cursor)
    const monthEnd = endOfMonth(cursor)
    return {
      gridStartIso: format(
        startOfWeek(monthStart, { weekStartsOn: WEEK_START }),
        'yyyy-MM-dd',
      ),
      gridEndIso: format(
        endOfWeek(monthEnd, { weekStartsOn: WEEK_START }),
        'yyyy-MM-dd',
      ),
    }
  }, [cursor])

  // Zakres zapytania obejmuje PEŁNE tygodnie (z weekendami), bo praca zapisana
  // w sobotę musi się gdzieś ujawnić — patrz WeekendActivityNotice niżej.
  // Sama siatka pokazuje tylko dni robocze; startOfWeek/endOfWeek gwarantują,
  // że każdy tydzień wnosi dokładnie pięć komórek, więc rzędy się nie rozjeżdżają.
  const days = useMemo(
    () =>
      eachDayOfInterval({
        start: parseISO(`${gridStartIso}T00:00:00`),
        end: parseISO(`${gridEndIso}T00:00:00`),
      }).filter((d) => !WEEKEND_DOW.has(d.getDay())),
    [gridStartIso, gridEndIso],
  )

  const { data, isLoading, error, isFetching } = useAdminSessions(
    { from: gridStartIso, to: gridEndIso },
    { keepPrevious: true },
  )

  const aggregates = useMemo(() => {
    const map = new Map<string, DayAggregate>()
    if (!data) return map
    for (const s of data.sessions) {
      const day = sessionDayIso(s)
      const entry = map.get(day) ?? {
        uniqueUsers: new Set<string>(),
        totalSeconds: 0,
        hasOpenSession: false,
      }
      entry.uniqueUsers.add(s.userId)
      if (s.endedAt) {
        const seconds =
          (parseISO(s.endedAt).getTime() - parseISO(s.startedAt).getTime()) /
          1000
        entry.totalSeconds += seconds
      } else {
        entry.hasOpenSession = true
      }
      map.set(day, entry)
    }
    return map
  }, [data])

  // Zakres siatki, nie rok kursora: styczniowa siatka zaczyna się w grudniu
  // poprzedniego roku, a filtr roczny działa po `startDate` — urlop
  // 22.12–09.01 nie wracał dla stycznia w ogóle.
  const vacationsQuery = useAdminVacationRequests(
    { status: 'APPROVED', from: gridStartIso, to: gridEndIso },
    { keepPrevious: true },
  )

  const vacationsByDay = useMemo(() => {
    const map = new Map<
      string,
      { userId: string; userName: string; type: string }[]
    >()
    const requests = vacationsQuery.data?.requests ?? []
    for (const day of days) {
      const key = format(day, 'yyyy-MM-dd')
      const matches = requests.filter(
        (r) => r.startDate <= key && r.endDate >= key,
      )
      if (matches.length > 0) {
        const seen = new Set<string>()
        const entries: { userId: string; userName: string; type: string }[] = []
        for (const r of matches) {
          if (seen.has(r.userId)) continue
          seen.add(r.userId)
          entries.push({ userId: r.userId, userName: r.user.name, type: r.type })
        }
        map.set(key, entries)
      }
    }
    return map
  }, [vacationsQuery.data, days])

  const weekendActivity = useMemo<WeekendActivityEntry[]>(
    () => weekendEntriesFrom(data?.sessions ?? [], gridStartIso, gridEndIso),
    [data, gridStartIso, gridEndIso],
  )

  // „Dziś" w widoku miesiąca dosuwa dzisiejszą komórkę do widoku —
  // przy długiej stronie na telefonie siatka bywa poza ekranem.
  useEffect(() => {
    if (focusToken === 0) return
    // W sobotę i niedzielę nie ma komórki „dzisiaj" (siatka pokazuje pn–pt),
    // więc dosuwamy samą siatkę — przycisk nigdy nie jest cichym no-opem.
    const target = todayCellRef.current ?? gridRef.current
    target?.scrollIntoView({
      block: 'nearest',
      behavior: reducedMotion ? 'auto' : 'smooth',
    })
  }, [focusToken, reducedMotion])

  return (
    <div className="space-y-3">
      {error && <Alert variant="error">{apiErrorMessage(error)}</Alert>}

      <div
        ref={gridRef}
        className="rounded-card border border-border-subtle bg-bg-elevated overflow-hidden"
      >
        <div className="grid grid-cols-5 bg-bg-surface text-text-muted text-[10px] sm:text-xs uppercase tracking-wider">
          {WEEKDAY_LABELS.map((d) => (
            <div key={d} className="px-1 sm:px-2 py-2 text-center">
              {d}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-5">
          {days.map((day, idx) => {
            const key = format(day, 'yyyy-MM-dd')
            const agg = aggregates.get(key)
            const vacations = vacationsByDay.get(key)
            const inMonth = isSameMonth(day, cursor)
            const isTodayCell = key === today
            const isFirstCol = idx % 5 === 0
            const isFirstRow = idx < 5
            const people = agg?.uniqueUsers.size ?? 0

            return (
              <Link
                key={key}
                to={`/admin/day/${key}`}
                ref={isTodayCell ? todayCellRef : undefined}
                aria-current={isTodayCell ? 'date' : undefined}
                className={cn(
                  'min-h-[68px] sm:min-h-[88px] p-1.5 sm:p-2',
                  'flex flex-col gap-0.5 sm:gap-1 transition-colors',
                  'hover:bg-bg-surface focus:bg-bg-surface',
                  !isFirstRow && 'border-t border-border-subtle',
                  !isFirstCol && 'border-l border-border-subtle',
                  !inMonth && 'opacity-45',
                  isTodayCell && 'ring-2 ring-brand-accent ring-inset',
                )}
              >
                <span className="sr-only">
                  {format(day, 'd.MM.yyyy')}
                  {isTodayCell ? ' — dzisiaj' : ''} —{' '}
                  {people === 0
                    ? 'brak sesji'
                    : `${pluralPersons(people)}, ${
                        formatHours(agg?.totalSeconds ?? 0) || 'sesja w trakcie'
                      }`}
                </span>

                <span
                  aria-hidden
                  className={cn(
                    'text-xs sm:text-sm font-medium tabular-nums',
                    isTodayCell && 'text-brand-accent',
                  )}
                >
                  {format(day, 'd')}
                </span>

                {agg && (
                  <>
                    {/* Telefon: komórka ma ~48 px, więc „12 osób" i „8h 30min"
                        łamały się na sylaby. Skrót mieści się w jednej linii. */}
                    <span
                      aria-hidden
                      className="sm:hidden text-[10px] leading-tight text-text-muted tabular-nums"
                    >
                      {people} os.
                    </span>
                    <span
                      aria-hidden
                      className="sm:hidden text-[10px] leading-tight font-mono tabular-nums text-text-primary"
                    >
                      {formatHoursCompact(agg.totalSeconds) ||
                        (agg.hasOpenSession ? '···' : '')}
                    </span>

                    <span
                      aria-hidden
                      className="hidden sm:block text-xs text-text-muted"
                    >
                      {pluralPersons(people)}
                    </span>
                    <span
                      aria-hidden
                      className="hidden sm:block text-xs font-medium font-mono"
                    >
                      {formatHours(agg.totalSeconds) ||
                        (agg.hasOpenSession ? 'w trakcie' : '')}
                    </span>
                  </>
                )}

                {vacations && vacations.length > 0 && (
                  <span
                    className="mt-auto inline-flex items-center gap-0.5 sm:gap-1 text-[10px] sm:text-[11px] text-text-muted self-end"
                    title={vacations
                      .map((v) => `${v.userName} — ${v.type}`)
                      .join('\n')}
                  >
                    <Palmtree className="h-3 w-3 shrink-0" aria-hidden />
                    <span className="tabular-nums">{vacations.length}</span>
                  </span>
                )}
              </Link>
            )
          })}
        </div>
      </div>

      <WeekendActivityNotice entries={weekendActivity} />

      {isLoading && (
        <p className="text-sm text-text-muted inline-flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Wczytywanie sesji…
        </p>
      )}
      {!isLoading && isFetching && (
        <p
          aria-live="polite"
          className="text-xs text-text-muted inline-flex items-center gap-2"
        >
          <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
          Odświeżam…
        </p>
      )}
    </div>
  )
}
